import { ChangeDetectionStrategy, Component, EventEmitter, Input, Output } from '@angular/core';
import { CommonModule } from '@angular/common';

// Presentational-only ("пульт управления") — bottom toolbar shown exclusively
// on mobile viewports (see ViewportService.isMobileLayout). Every button calls
// back into AppComponent methods that ALSO serve the desktop keyboard handler
// (togglePlayPause, setClipStartToPlayhead/setClipEndToPlayhead, seekRelative,
// undo/redo = Ctrl+Z / Ctrl+Y) — this component owns no playback/clip state of its own.
// The undo toast replaces the buttons' labels row for 2.5s: same grid cell, so the bar's
// height (and the page's pb-16 clearance) never changes.
@Component({
  selector: 'app-mobile-control-bar',
  standalone: true,
  imports: [CommonModule],
  changeDetection: ChangeDetectionStrategy.OnPush,
  host: {
    'class': 'block'
  },
  template: `
    <div class="grid bg-gray-900/95 backdrop-blur-sm border-t border-gray-800 shadow-2xl shadow-black/60">
    <div class="col-start-1 row-start-1 flex items-stretch justify-around px-1 py-1.5 gap-1">
      <button (click)="seekBack.emit()" class="flex-1 flex flex-col items-center justify-center gap-0.5 py-1.5 rounded text-gray-300 active:bg-gray-800 transition-colors" aria-label="Назад 10 секунд">
        <svg xmlns="http://www.w3.org/2000/svg" class="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor" stroke-width="2"><path stroke-linecap="round" stroke-linejoin="round" d="M11 19l-7-7 7-7m8 14l-7-7 7-7" /></svg>
        <span class="text-[9px] leading-none" [class.invisible]="!!toast">-10с</span>
      </button>

      <button (click)="playPauseToggle.emit()" class="flex-1 flex flex-col items-center justify-center gap-0.5 py-1.5 rounded text-indigo-300 active:bg-gray-800 transition-colors" [attr.aria-label]="isPlaying ? 'Пауза' : 'Воспроизвести'">
        @if (isPlaying) {
          <svg xmlns="http://www.w3.org/2000/svg" class="h-6 w-6" viewBox="0 0 20 20" fill="currentColor"><path fill-rule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.5 8a.5.5 0 00-1 0v4a.5.5 0 001 0V8zm3 0a.5.5 0 00-1 0v4a.5.5 0 001 0V8z" clip-rule="evenodd" /></svg>
        } @else {
          <svg xmlns="http://www.w3.org/2000/svg" class="h-6 w-6" viewBox="0 0 20 20" fill="currentColor"><path fill-rule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM9.555 7.168A1 1 0 008 8v4a1 1 0 001.555.832l3-2a1 1 0 000-1.664l-3-2z" clip-rule="evenodd" /></svg>
        }
        <span class="text-[9px] leading-none" [class.invisible]="!!toast">{{ isPlaying ? 'пауза' : 'играть' }}</span>
      </button>

      <button (click)="seekForward.emit()" class="flex-1 flex flex-col items-center justify-center gap-0.5 py-1.5 rounded text-gray-300 active:bg-gray-800 transition-colors" aria-label="Вперёд 10 секунд">
        <svg xmlns="http://www.w3.org/2000/svg" class="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor" stroke-width="2"><path stroke-linecap="round" stroke-linejoin="round" d="M13 5l7 7-7 7M5 5l7 7-7 7" /></svg>
        <span class="text-[9px] leading-none" [class.invisible]="!!toast">+10с</span>
      </button>

      <div class="w-px bg-gray-800 my-1"></div>

      <button (click)="setClipStart.emit()" class="flex-1 flex flex-col items-center justify-center gap-0.5 py-1.5 rounded text-gray-300 active:bg-gray-800 transition-colors" aria-label="Начало клипа — текущая позиция">
        <svg xmlns="http://www.w3.org/2000/svg" class="h-5 w-5" viewBox="0 0 20 20" fill="currentColor"><path fill-rule="evenodd" d="M4 3a1 1 0 011 1v12a1 1 0 11-2 0V4a1 1 0 011-1zm3.293 1.293a1 1 0 011.414 0l6 6a1 1 0 010 1.414l-6 6a1 1 0 01-1.414-1.414L12.586 10 7.293 4.707a1 1 0 010-1.414z" clip-rule="evenodd" /></svg>
        <span class="text-[9px] leading-none" [class.invisible]="!!toast">начало</span>
      </button>

      <button (click)="setClipEnd.emit()" class="flex-1 flex flex-col items-center justify-center gap-0.5 py-1.5 rounded text-gray-300 active:bg-gray-800 transition-colors" aria-label="Конец клипа — текущая позиция">
        <svg xmlns="http://www.w3.org/2000/svg" class="h-5 w-5" viewBox="0 0 20 20" fill="currentColor"><path fill-rule="evenodd" d="M16 3a1 1 0 00-1 1v12a1 1 0 102 0V4a1 1 0 00-1-1zm-3.293 1.293a1 1 0 00-1.414 0l-6 6a1 1 0 000 1.414l6 6a1 1 0 001.414-1.414L7.414 10l5.293-5.293a1 1 0 000-1.414z" clip-rule="evenodd" /></svg>
        <span class="text-[9px] leading-none" [class.invisible]="!!toast">конец</span>
      </button>

      <div class="w-px bg-gray-800 my-1"></div>

      <button (click)="undo.emit()" [disabled]="!canUndo" class="flex-1 flex flex-col items-center justify-center gap-0.5 py-1.5 rounded text-gray-300 active:bg-gray-800 disabled:opacity-30 transition-colors" aria-label="Отменить">
        <svg xmlns="http://www.w3.org/2000/svg" class="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor" stroke-width="2" aria-hidden="true"><path stroke-linecap="round" stroke-linejoin="round" d="M9 15L3 9m0 0l6-6M3 9h12a6 6 0 010 12h-3" /></svg>
        <span class="text-[9px] leading-none" [class.invisible]="!!toast">отмена</span>
      </button>

      <button (click)="redo.emit()" [disabled]="!canRedo" class="flex-1 flex flex-col items-center justify-center gap-0.5 py-1.5 rounded text-gray-300 active:bg-gray-800 disabled:opacity-30 transition-colors" aria-label="Повторить">
        <svg xmlns="http://www.w3.org/2000/svg" class="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor" stroke-width="2" aria-hidden="true"><path stroke-linecap="round" stroke-linejoin="round" d="M15 15l6-6m0 0l-6-6m6 6H9a6 6 0 000 12h3" /></svg>
        <span class="text-[9px] leading-none" [class.invisible]="!!toast">повтор</span>
      </button>

      <div class="w-px bg-gray-800 my-1"></div>

      <button (click)="toggleChat.emit()" class="flex-1 flex flex-col items-center justify-center gap-0.5 py-1.5 rounded transition-colors active:bg-gray-800" [class.text-indigo-400]="isChatOpen" [class.text-gray-300]="!isChatOpen" aria-label="Чат">
        <svg xmlns="http://www.w3.org/2000/svg" class="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor" stroke-width="2"><path stroke-linecap="round" stroke-linejoin="round" d="M8 10h.01M12 10h.01M16 10h.01M9 16H5a2 2 0 01-2-2V6a2 2 0 012-2h14a2 2 0 012 2v8a2 2 0 01-2 2h-5l-5 5v-5z" /></svg>
        <span class="text-[9px] leading-none" [class.invisible]="!!toast">чат</span>
      </button>

      <button (click)="jumpToHighlights.emit()" class="flex-1 flex flex-col items-center justify-center gap-0.5 py-1.5 rounded text-gray-300 active:bg-gray-800 transition-colors" aria-label="К списку моментов">
        <svg xmlns="http://www.w3.org/2000/svg" class="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor" stroke-width="2"><path stroke-linecap="round" stroke-linejoin="round" d="M4 6h16M4 12h16M4 18h7" /></svg>
        <span class="text-[9px] leading-none" [class.invisible]="!!toast">моменты</span>
      </button>
    </div>
    <div role="status" aria-live="polite" class="col-start-1 row-start-1 self-end mb-3 px-3 text-center text-[10px] leading-[10px] text-gray-100 truncate pointer-events-none">{{ toast }}</div>
    </div>
  `,
})
export class MobileControlBarComponent {
  @Input() isPlaying = false;
  @Input() isChatOpen = false;
  @Input() canUndo = false;
  @Input() canRedo = false;
  @Input() toast: string | null = null;

  @Output() playPauseToggle = new EventEmitter<void>();
  @Output() seekBack = new EventEmitter<void>();
  @Output() seekForward = new EventEmitter<void>();
  @Output() setClipStart = new EventEmitter<void>();
  @Output() setClipEnd = new EventEmitter<void>();
  @Output() toggleChat = new EventEmitter<void>();
  @Output() jumpToHighlights = new EventEmitter<void>();
  @Output() undo = new EventEmitter<void>();
  @Output() redo = new EventEmitter<void>();
}
